// Source pixels from CCACHE's matched road drivetrain assemblies. Straight chain
// tails are clipped at these seams; the renderer joins them for each frame.
// `pivot` is the true spindle centre (crank-bolt bore / tooth-circle fit) the
// split crank layers turn about; see scripts/split-ccache-cranks.py. `pedal`
// is the centre of the pedal eye at the end of the crank arm.
// `chain` replaces the photographed chain with one drawn loop:
// the big ring about the pivot, then cog, upper and lower pulley in rear
// pixels, fitted to the photo; `layers` names its chain-free cutouts and the
// rear derailleur parts that sit in front of the chain.
const profiles = {
  force: {
    asset: 'sram-force',
    front: { origin: { x: 480, y: 357 }, pivot: { x: 472.3, y: 364.1 }, pedal: { x: 843.9, y: 358.9 }, seam: 180, top: 150, bottom: 570 },
    rear: { origin: { x: 151, y: 158 }, seam: 400, top: 90, bottom: 526 },
    chain: { ring: 194, cog: { x: 151, y: 158, r: 72 }, upper: { x: 245, y: 320, r: 45 }, lower: { x: 262, y: 462, r: 54 }, pitch: 25, width: 14 },
    layers: { rear: 'drivetrain-rear-clean', over: 'drivetrain-rear-over', static: 'drivetrain-front-fd' }
  },
  red: {
    asset: 'sram-red',
    front: { origin: { x: 480, y: 386 }, pivot: { x: 476.5, y: 385.6 }, pedal: { x: 842.8, y: 381.8 }, seam: 180, top: 179, bottom: 597 },
    rear: { origin: { x: 168, y: 158 }, seam: 400, top: 91, bottom: 525 },
    chain: { ring: 200, cog: { x: 168, y: 160, r: 72 }, upper: { x: 218, y: 322, r: 49 }, lower: { x: 133, y: 468, r: 57 }, pitch: 25.7, width: 14 },
    layers: { rear: 'drivetrain-rear-clean', over: 'drivetrain-rear-over', static: 'drivetrain-front-fd' }
  },
  dura: {
    asset: 'shimano-dura-ace',
    front: { origin: { x: 386, y: 333 }, pivot: { x: 390.7, y: 332.8 }, pedal: { x: 745.5, y: 319 }, seam: 140, top: 131, bottom: 550 },
    rear: { origin: { x: 136, y: 117 }, seam: 400, top: 61, bottom: 479 },
    chain: { ring: 188, cog: { x: 134, y: 119, r: 59 }, upper: { x: 210, y: 292, r: 36 }, lower: { x: 192, y: 437, r: 43 }, pitch: 24.6, width: 14 },
    layers: { rear: 'drivetrain-rear-clean', over: 'drivetrain-rear-over', static: 'drivetrain-front-fd' }
  }
};

export function drivetrainProfile(name = '') {
  // Never substitute a road double for a gravel/single-ring or older Force kit.
  if (/\b(?:1x|xplr)\b/i.test(name)) return null;
  if (/sram.*force.*e1.*2x/i.test(name)) return profiles.force;
  if (/sram.*red.*2x/i.test(name)) return profiles.red;
  if (/shimano.*dura.?ace.*r9200/i.test(name)) return profiles.dura;
  return null;
}

export function drivetrainLayers(profile, base, wheelScale = 1) {
  const scale = .34 * wheelScale;
  const image = part => `/resources/groupsets/ccache/${profile.asset}-${part}.png`;
  const layers = profile.layers || {};
  return [
    { ...base, type: 'groupsetsLeft', anchor: 'groupsetsLeft', index: 4, scale,
      origin: { ...profile.rear.origin }, image: image(layers.rear || 'drivetrain-rear'),
      cropRight: profile.chain ? null : profile.rear.seam, drivetrain: profile },
    // The drawn chain passes behind the rear derailleur's body and cage.
    ...(layers.over ? [{ ...base, type: 'groupsetsRearOver', anchor: 'groupsetsLeft', index: 10.2, scale,
      origin: { ...profile.rear.origin }, image: image(layers.over), drivetrain: profile }] : []),
    // Front assembly in three layers: chainrings turn under the static front
    // derailleur and chain, the crank arm turns on top of both.
    ...[['drivetrain-front-rings', 'groupsetsMiddle', 10, true],
      [layers.static || 'drivetrain-front-static', 'groupsetsFrontStatic', 10.4, false],
      ['drivetrain-front-arm', 'groupsetsCrankArm', 10.6, true]].map(([part, type, index, turns]) => ({
      ...base, type, anchor: 'groupsetsMiddle', index, scale,
      origin: { ...profile.front.origin }, pivot: turns ? { ...profile.front.pivot } : null,
      image: image(part), cropLeft: profile.front.seam, drivetrain: profile, turns }))
  ];
}

export function chainSeams(front, rear, lower = false) {
  if (!front || !rear || !front.drivetrain || front.drivetrain !== rear.drivetrain) return null;
  const profile = front.drivetrain;
  return [[rear, profile.rear], [front, profile.front]].map(([item, part]) => ({
    x: item.x + (part.seam - item.origin.x) * item.scale,
    y: item.y + (part[lower ? 'bottom' : 'top'] - item.origin.y) * item.scale
  }));
}

// Belt tangent from sprocket a to b ({x, y, r, dir}; dir 1 turns clockwise on
// screen, -1 anticlockwise): the one the chain runs along in travel order.
function beltTangent(a, b) {
  const dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy), base = Math.atan2(dy, dx);
  for (const sa of [1, -1]) for (const sb of [1, -1]) for (const side of [1, -1]) {
    const ra = a.r * sa, rb = b.r * sb, c = (ra - rb) / length;
    if (Math.abs(c) > 1) continue;
    const phi = base + side * Math.acos(c), nx = Math.cos(phi), ny = Math.sin(phi);
    const p = { x: a.x + ra * nx, y: a.y + ra * ny }, q = { x: b.x + rb * nx, y: b.y + rb * ny };
    const tx = q.x - p.x, ty = q.y - p.y;
    const va = a.dir * ((p.x - a.x) * ty - (p.y - a.y) * tx), vb = b.dir * ((q.x - b.x) * ty - (q.y - b.y) * tx);
    if (va > 0 && vb > 0) return [p, q];
  }
  return null;
}

// Closed chain centreline around the sprockets, in travel order.
export function chainLoop(sprockets) {
  const n = sprockets.length;
  const tangents = sprockets.map((a, i) => beltTangent(a, sprockets[(i + 1) % n]));
  if (tangents.some(t => !t)) return null;
  const points = [];
  sprockets.forEach((s, i) => {
    const arrive = tangents[(i + n - 1) % n][1], leave = tangents[i][0];
    const start = Math.atan2(arrive.y - s.y, arrive.x - s.x);
    let end = Math.atan2(leave.y - s.y, leave.x - s.x);
    while (s.dir > 0 ? end < start : end > start) end += s.dir * Math.PI * 2;
    points.push(...arcPoints(s.x, s.y, s.r, start, end));
  });
  points.push({ ...points[0] });
  return points;
}

// Pins evenly round a closed loop (an even count, like a real chain), slid
// `shift` along it. Each pin carries its link parity so plates never flicker.
export function loopPins(points, pitch, shift = 0) {
  const lengths = points.slice(1).map((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (!Number.isFinite(total) || total <= 0 || !(pitch > 0)) return [];
  const count = Math.max(2, 2 * Math.round(total / pitch / 2)), step = total / count;
  const turns = Math.floor(shift / step), offset = (shift - turns * step);
  const pins = [];
  let segment = 0, start = 0;
  for (let i = 0; i < count; i++) {
    const distance = offset + i * step;
    while (segment < lengths.length - 1 && start + lengths[segment] < distance) start += lengths[segment++];
    const ratio = lengths[segment] ? (distance - start) / lengths[segment] : 0;
    pins.push({ x: points[segment].x + (points[segment + 1].x - points[segment].x) * ratio,
      y: points[segment].y + (points[segment + 1].y - points[segment].y) * ratio,
      outer: ((i - turns) % 2 + 2) % 2 === 0 });
  }
  return pins;
}

// Photo-matched road chain: satin inner links under brighter outer plates,
// dark rollers between them and polished pin heads.
export function drawChainLoop(ctx, pins, width) {
  if (pins.length < 2) return;
  const r = width / 2;
  const plate = (a, b, waist) => {
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    ctx.save();
    ctx.translate(a.x, a.y);
    ctx.rotate(Math.atan2(b.y - a.y, b.x - a.x));
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.quadraticCurveTo(length / 2, -r * waist, length, -r);
    ctx.arc(length, 0, r, -Math.PI / 2, Math.PI / 2);
    ctx.quadraticCurveTo(length / 2, r * waist, 0, r);
    ctx.arc(0, 0, r, Math.PI / 2, Math.PI * 1.5);
    ctx.closePath();
    ctx.fill(); ctx.stroke();
    ctx.restore();
  };
  const links = pins.map((a, i) => [a, pins[(i + 1) % pins.length]]);
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineWidth = width * .1;
  ctx.fillStyle = '#9da19c'; ctx.strokeStyle = '#5e635e';
  links.forEach(([a, b]) => { if (!a.outer) plate(a, b, .62); });
  ctx.fillStyle = '#3a3f3c';
  for (const pin of pins) { ctx.beginPath(); ctx.arc(pin.x, pin.y, r * .62, 0, Math.PI * 2); ctx.fill(); }
  ctx.fillStyle = '#d3d5cf'; ctx.strokeStyle = '#6c716c';
  links.forEach(([a, b]) => { if (a.outer) plate(a, b, .5); });
  for (const pin of pins) {
    ctx.beginPath(); ctx.arc(pin.x, pin.y, r * .4, 0, Math.PI * 2);
    ctx.fillStyle = '#5b605c'; ctx.fill();
    ctx.beginPath(); ctx.arc(pin.x - r * .08, pin.y - r * .1, r * .2, 0, Math.PI * 2);
    ctx.fillStyle = '#eceee8'; ctx.fill();
  }
  ctx.restore();
}

// The whole chain loop in canvas pixels for a profile with a drawn chain:
// ring about the crank pivot, then the rear sprockets, in pedalling order.
export function drivetrainChain(front, rear) {
  if (!front || !rear || !front.drivetrain || front.drivetrain !== rear.drivetrain || !front.drivetrain.chain) return null;
  const { front: crank, chain } = front.drivetrain;
  const at = (item, p) => ({ x: item.x + (p.x - item.origin.x) * item.scale, y: item.y + (p.y - item.origin.y) * item.scale });
  const sprocket = (item, p, dir) => ({ ...at(item, p), r: p.r * item.scale, dir });
  const loop = chainLoop([
    sprocket(front, { ...crank.pivot, r: chain.ring }, 1),
    sprocket(rear, chain.lower, 1), sprocket(rear, chain.upper, -1), sprocket(rear, chain.cog, 1)
  ]);
  return loop && { loop, pitch: chain.pitch * front.scale, width: chain.width * front.scale };
}

export function arcPoints(x, y, radius, start, end) {
  const count = Math.max(4, Math.ceil(Math.abs(end - start) * radius / 3));
  return Array.from({ length: count + 1 }, (_, i) => {
    const angle = start + (end - start) * i / count;
    return { x: x + radius * Math.cos(angle), y: y + radius * Math.sin(angle) };
  });
}

// Equal-distance sampling keeps links uniform on arcs and sloping chain runs.
// `shift` slides the pins along the run (pedalling), clamped at both ends.
export function chainPins(points, pitch, shift = 0) {
  const lengths = points.slice(1).map((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (!Number.isFinite(total) || total <= 0 || !(pitch > 0)) return [];
  const count = Math.max(1, Math.round(total / pitch));
  const step = total / count, lead = shift ? 1 : 0, frac = shift / step - Math.floor(shift / step);
  let segment = 0, offset = 0;
  return Array.from({ length: count + 1 + lead }, (_, i) => {
    const distance = Math.min(total, Math.max(0, (i - lead + (lead && frac)) * step));
    while (segment < lengths.length - 1 && offset + lengths[segment] < distance) offset += lengths[segment++];
    const ratio = lengths[segment] ? (distance - offset) / lengths[segment] : 0;
    return { x: points[segment].x + (points[segment + 1].x - points[segment].x) * ratio,
      y: points[segment].y + (points[segment + 1].y - points[segment].y) * ratio };
  });
}

export function drawChainLinks(ctx, points, pitch = 6, width = 3.4, shift = 0) {
  const pins = chainPins(points, pitch, shift);
  if (pins.length < 2) return;
  // Keep plate colours travelling with the links rather than flickering.
  const total = points.slice(1).reduce((sum, p, i) => sum + Math.hypot(p.x - points[i].x, p.y - points[i].y), 0);
  const parity = shift ? Math.floor(shift / (total / Math.max(1, Math.round(total / pitch)))) + 1 : 0;
  ctx.save();
  // Alternating steel plates, narrowed waists and dark recessed rivets.
  for (let i = 1; i < pins.length; i++) {
    const a = pins[i - 1], b = pins[i], length = Math.hypot(b.x - a.x, b.y - a.y), r = width / 2;
    ctx.save();
    ctx.translate(a.x, a.y);
    ctx.rotate(Math.atan2(b.y - a.y, b.x - a.x));
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.quadraticCurveTo(length / 2, -r * .4, length, -r);
    ctx.arc(length, 0, r, -Math.PI / 2, Math.PI / 2);
    ctx.quadraticCurveTo(length / 2, r * .4, 0, r);
    ctx.arc(0, 0, r, Math.PI / 2, Math.PI * 1.5);
    ctx.closePath();
    ctx.fillStyle = (i + parity) % 2 ? '#bcbeb8' : '#8b8f8b';
    ctx.strokeStyle = '#626862';
    ctx.lineWidth = width * .15;
    ctx.fill(); ctx.stroke();
    ctx.restore();
  }
  for (const pin of pins) {
    ctx.beginPath(); ctx.arc(pin.x, pin.y, width * .25, 0, Math.PI * 2);
    ctx.fillStyle = '#454c48'; ctx.fill();
    ctx.beginPath(); ctx.arc(pin.x - width * .045, pin.y - width * .065, width * .11, 0, Math.PI * 2);
    ctx.fillStyle = '#e0e2d9'; ctx.fill();
  }
  ctx.restore();
}

// Calibration of the existing catalogue cutouts. Origins are actual spindle,
// axle and tape/hood junctions, not the centre of the padded image canvas.
// All cranks use the same 175 mm display reference and .70 logical px/mm.
const catalogueGeometry = {
  campagnolo: {
    front: { x: 242, y: 246, pedalX: 650, pedal: { x: 651.5, y: 243 }, radius: 225, width: 691, height: 491 },
    rear: { x: 171, y: 347, scale: .31, radius: 58, exitX: 230, exitY: 705, width: 356, height: 720 }
  },
  ultegra: {
    front: { x: 242, y: 246, pedalX: 651, pedal: { x: 650.8, y: 243.7 }, radius: 238, width: 691, height: 491 },
    rear: { x: 175, y: 348, scale: .28, radius: 70, exitX: 104, exitY: 696, width: 356, height: 720 }
  },
  forceXplr: {
    front: { x: 243, y: 246, pedalX: 674, pedal: { x: 669.4, y: 243.9 }, radius: 207, width: 740, height: 491 },
    rear: { x: 174, y: 437, scale: .39, radius: 68, exitX: 190, exitY: 881, width: 356, height: 912, splitY: 600, derailleurOffsetY: -105 }
  },
  redXplr: {
    front: { x: 213, y: 248, pedalX: 650, pedal: { x: 644.6, y: 246.7 }, radius: 198, width: 691, height: 491 },
    rear: { x: 177, y: 344, scale: .38, radius: 70, exitX: 145, exitY: 691, width: 356, height: 720 }
  }
};

export function catalogueCalibration(name = '') {
  if (/sram.*force.*e1.*1x.*xplr/i.test(name)) return catalogueGeometry.forceXplr;
  if (/sram.*red.*1x.*xplr/i.test(name)) return catalogueGeometry.redXplr;
  if (/shimano.*ultegra.*r8100/i.test(name)) return catalogueGeometry.ultegra;
  if (/campagnolo.*super record.*wireless/i.test(name)) return catalogueGeometry.campagnolo;
  return null;
}

export function calibrateCatalogueLayer(layer, geometry, wheelScale = 1) {
  if (!geometry) return layer;
  const calibrated = { ...layer, calibration: geometry };
  const part = layer.type === 'groupsetsMiddle' ? geometry.front :
    layer.type === 'groupsetsLeft' ? geometry.rear : null;
  if (part) {
    calibrated.origin = { x: part.x, y: part.y };
    if (part.splitY) calibrated.rearImageSplit = { y: part.splitY, offsetY: part.derailleurOffsetY };
    calibrated.renderWidth = part.width || 330;
    calibrated.renderHeight = part.height || 460;
    calibrated.scale = (part.pedalX ? 175 * .70 / (part.pedalX - part.x) : part.scale) * wheelScale;
  } else {
    // Rotors, calipers and front derailleurs must follow the same frame scale.
    calibrated.scale *= wheelScale;
  }
  return calibrated;
}

// Where the drive-side pedal mounts: the pedal eye in the source pixels of the
// crank layer that carries it, or null when the crank artwork is uncalibrated.
export function pedalMount(profile, calibration) {
  if (profile && profile.front.pedal) return { ...profile.front.pedal, type: 'groupsetsCrankArm' };
  if (calibration && calibration.front.pedal) return { ...calibration.front.pedal, type: 'groupsetsMiddle' };
  return null;
}

export function catalogueChain(front, rear) {
  if (!front || !rear || !front.calibration || front.calibration !== rear.calibration) return null;
  const { front: crank, rear: cassette } = front.calibration;
  return {
    frontRadius: crank.radius * front.scale,
    rearRadius: cassette.radius * rear.scale,
    lowerRear: { x: rear.x + (cassette.exitX - rear.origin.x) * rear.scale,
      y: rear.y + (cassette.exitY + (cassette.derailleurOffsetY || 0) - rear.origin.y) * rear.scale }
  };
}
